import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * CreateInvoiceModal - Admin modal for creating invoices manually
 *
 * Allows admins to:
 * - Select a customer from phonebook
 * - Optionally link to an existing memo
 * - Add line items (diamonds or custom)
 * - Set payment terms
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTimes, FaSearch, FaPlus, FaTrash, FaUser, FaFileInvoice, FaLink } from 'react-icons/fa';
import { apiClient } from '../utils/apiClient';
import { notify } from '../utils/notifications';
import mockDataService from '../services/mockDataService';
const Overlay = styled.div `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
`;
const Modal = styled.div `
    background: var(--bg-primary);
    border-radius: 12px;
    width: 100%;
    max-width: 800px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border-color);
`;
const Header = styled.div `
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 3px solid var(--accent-gold);
    background: var(--bg-secondary);
`;
const Title = styled.h2 `
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
`;
const CloseButton = styled.button `
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 0.5rem;
    font-size: 1.25rem;

    &:hover {
        color: var(--text-primary);
    }
`;
const Content = styled.div `
    padding: 1.5rem;
    overflow-y: auto;
    flex: 1;
`;
const Section = styled.div `
    margin-bottom: 1.5rem;

    &:last-child {
        margin-bottom: 0;
    }
`;
const SectionTitle = styled.h3 `
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 1rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
        color: var(--accent-gold);
    }
`;
const FormGrid = styled.div `
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;
const FormGroup = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;
const Label = styled.label `
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-secondary);
`;
const Input = styled.input `
    padding: 0.625rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--bg-primary);
    color: var(--text-primary);

    &:focus {
        outline: none;
        border-color: var(--accent-gold);
    }

    &::placeholder {
        color: var(--text-tertiary);
    }
`;
const Select = styled.select `
    padding: 0.625rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--bg-primary);
    color: var(--text-primary);

    &:focus {
        outline: none;
        border-color: var(--accent-gold);
    }
`;
const Textarea = styled.textarea `
    padding: 0.625rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--bg-primary);
    color: var(--text-primary);
    resize: vertical;
    min-height: 60px;

    &:focus {
        outline: none;
        border-color: var(--accent-gold);
    }

    &::placeholder {
        color: var(--text-tertiary);
    }
`;
const SearchContainer = styled.div `
    position: relative;
`;
const SearchInput = styled(Input) `
    padding-left: 2.5rem;
    width: 100%;
`;
const SearchIcon = styled.div `
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-tertiary);
`;
const Dropdown = styled.div `
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 10;
    margin-top: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;
const DropdownItem = styled.div `
    padding: 0.75rem 1rem;
    cursor: pointer;
    border-bottom: 1px solid var(--border-color);

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: var(--bg-tertiary);
    }

    .name {
        font-weight: 500;
        color: var(--text-primary);
    }

    .detail {
        font-size: 0.75rem;
        color: var(--text-tertiary);
    }
`;
const SelectedItem = styled.div `
    padding: 0.75rem 1rem;
    background: var(--bg-tertiary);
    border: 1px solid var(--accent-gold);
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .info {
        .name {
            font-weight: 500;
            color: var(--text-primary);
        }
        .detail {
            font-size: 0.75rem;
            color: var(--text-tertiary);
        }
    }

    button {
        background: none;
        border: none;
        color: var(--text-tertiary);
        cursor: pointer;
        padding: 0.25rem;

        &:hover {
            color: #ef4444;
        }
    }
`;
const LineItemsTable = styled.div `
    border: 1px solid var(--border-color);
    border-radius: 6px;
    overflow: hidden;
`;
const LineItemHeader = styled.div `
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr auto;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: var(--bg-tertiary);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;

    @media (max-width: 600px) {
        display: none;
    }
`;
const LineItemRow = styled.div `
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr auto;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border-color);
    align-items: center;

    &:last-child {
        border-bottom: none;
    }

    input {
        padding: 0.5rem;
        border: 1px solid var(--border-color);
        border-radius: 4px;
        font-size: 0.8rem;
        background: var(--bg-primary);
        color: var(--text-primary);
        width: 100%;

        &:focus {
            outline: none;
            border-color: var(--accent-gold);
        }
    }

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
        gap: 0.75rem;
    }
`;
const DeleteButton = styled.button `
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    border-radius: 4px;
    padding: 0.4rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: rgba(239, 68, 68, 0.2);
    }
`;
const AddButton = styled.button `
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    background: var(--bg-tertiary);
    border: 1px dashed var(--border-color);
    color: var(--text-secondary);
    border-radius: 6px;
    cursor: pointer;
    margin-top: 0.75rem;
    font-size: 0.875rem;

    &:hover {
        background: var(--bg-secondary);
        color: var(--text-primary);
    }
`;
const TotalsSection = styled.div `
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.5rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--border-color);

    .row {
        display: flex;
        gap: 2rem;
        font-size: 0.875rem;

        .label {
            color: var(--text-secondary);
            min-width: 100px;
            text-align: right;
        }

        .value {
            color: var(--text-primary);
            font-weight: 500;
            min-width: 100px;
            text-align: right;
        }

        &.total {
            font-size: 1.1rem;
            font-weight: 600;

            .value {
                color: var(--accent-gold);
            }
        }
    }
`;
const Footer = styled.div `
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--border-color);
    background: var(--bg-secondary);
`;
const Button = styled.button `
    padding: 0.625rem 1.25rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    ${props => props.primary ? `
        background: var(--accent-gold);
        border: 1px solid var(--accent-gold);
        color: white;

        &:hover:not(:disabled) {
            background: var(--accent-gold-dark);
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    ` : `
        background: var(--bg-tertiary);
        border: 1px solid var(--border-color);
        color: var(--text-primary);

        &:hover {
            background: var(--bg-secondary);
        }
    `}
`;
export const CreateInvoiceModal = ({ isOpen, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [memos, setMemos] = useState([]);
    const [contactSearch, setContactSearch] = useState('');
    const [showContactDropdown, setShowContactDropdown] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [selectedMemo, setSelectedMemo] = useState(null);
    const [memoSearch, setMemoSearch] = useState('');
    const [showMemoDropdown, setShowMemoDropdown] = useState(false);
    const [lineItems, setLineItems] = useState([
        { id: '1', description: '', quantity: 1, unitPrice: 0, total: 0 }
    ]);
    const [paymentTerms, setPaymentTerms] = useState('Net 30');
    const [notes, setNotes] = useState('');
    useEffect(() => {
        if (isOpen) {
            fetchContacts();
            fetchMemos();
        }
    }, [isOpen]);
    const fetchContacts = async () => {
        try {
            if (mockDataService.isMockMode()) {
                setContacts(mockDataService.getContacts());
            }
            else {
                const response = await apiClient.get('contacts-list');
                if (response.ok && response.data?.contacts) {
                    setContacts(response.data.contacts);
                }
            }
        }
        catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };
    const fetchMemos = async () => {
        try {
            if (mockDataService.isMockMode()) {
                // Get approved or sold memos that could be invoiced
                const allMemos = mockDataService.getMemos();
                setMemos(allMemos.filter(m => m.status === 'approved' || m.status === 'sold'));
            }
            else {
                const response = await apiClient.get('memos/all-v2');
                if (response.ok && response.data?.memos) {
                    setMemos(response.data.memos.filter(m => m.status === 'approved' || m.status === 'sold'));
                }
            }
        }
        catch (error) {
            console.error('Error fetching memos:', error);
        }
    };
    const filteredContacts = contacts.filter(c => {
        if (!contactSearch)
            return false;
        const search = contactSearch.toLowerCase();
        return (c.companyName?.toLowerCase().includes(search) ||
            c.contactPerson?.toLowerCase().includes(search) ||
            c.email?.toLowerCase().includes(search));
    });
    const filteredMemos = memos.filter(m => {
        if (!memoSearch)
            return false;
        const search = memoSearch.toLowerCase();
        return (m.dealerName?.toLowerCase().includes(search) ||
            m.dealerEmail?.toLowerCase().includes(search) ||
            m.diamondDetails?.shape?.toLowerCase().includes(search) ||
            m.diamondDetails?.stockNumber?.toLowerCase().includes(search) ||
            String(m.diamondDetails?.caratWeight).includes(search));
    });
    const handleSelectContact = (contact) => {
        setSelectedContact(contact);
        setContactSearch('');
        setShowContactDropdown(false);
    };
    const handleSelectMemo = (memo) => {
        setSelectedMemo(memo);
        setMemoSearch('');
        setShowMemoDropdown(false);
        // Auto-fill contact if not selected
        if (!selectedContact) {
            const contact = contacts.find(c => c.email === memo.dealerEmail || c.id === memo.dealerId);
            if (contact) {
                setSelectedContact(contact);
            }
        }
        // Auto-fill line items from memo
        setLineItems([{
                id: '1',
                description: `${memo.diamondDetails.caratWeight}ct ${memo.diamondDetails.shape} ${memo.diamondDetails.color} ${memo.diamondDetails.clarity} Diamond`,
                quantity: 1,
                unitPrice: memo.diamondDetails.priceUSD,
                total: memo.diamondDetails.priceUSD
            }]);
    };
    const handleClearMemo = () => {
        setSelectedMemo(null);
        setMemoSearch('');
    };
    const handleLineItemChange = (id, field, value) => {
        setLineItems(items => items.map(item => {
            if (item.id === id) {
                const updated = { ...item, [field]: value };
                if (field === 'quantity' || field === 'unitPrice') {
                    updated.total = Number(updated.quantity) * Number(updated.unitPrice);
                }
                return updated;
            }
            return item;
        }));
    };
    const addLineItem = () => {
        setLineItems(items => [
            ...items,
            { id: Date.now().toString(), description: '', quantity: 1, unitPrice: 0, total: 0 }
        ]);
    };
    const removeLineItem = (id) => {
        if (lineItems.length > 1) {
            setLineItems(items => items.filter(item => item.id !== id));
        }
    };
    const calculateSubtotal = () => lineItems.reduce((sum, item) => sum + item.total, 0);
    const calculateTotal = () => calculateSubtotal();
    const handleSubmit = async () => {
        if (!selectedContact) {
            notify.error('Error', 'Please select a customer');
            return;
        }
        const validItems = lineItems.filter(item => item.description && item.total > 0);
        if (validItems.length === 0) {
            notify.error('Error', 'Please add at least one line item');
            return;
        }
        setLoading(true);
        try {
            const invoiceData = {
                customerId: selectedContact.id,
                customerEmail: selectedContact.email,
                customerName: selectedContact.contactPerson || selectedContact.companyName,
                customerBusinessName: selectedContact.companyName,
                customerAddress: selectedContact.address ?
                    `${selectedContact.address.street}, ${selectedContact.address.city}, ${selectedContact.address.state} ${selectedContact.address.zipCode}` : undefined,
                customerPhone: selectedContact.phone,
                memoId: selectedMemo?.id,
                lineItems: validItems.map(item => ({
                    description: item.description,
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                    total: item.total
                })),
                subtotal: calculateSubtotal(),
                total: calculateTotal(),
                paymentTerms,
                notes
            };
            if (mockDataService.isMockMode()) {
                mockDataService.createInvoice(invoiceData);
                notify.success('Success', 'Invoice created successfully');
                onSuccess();
                handleClose();
            }
            else {
                const response = await apiClient.post('invoices/create-manual', invoiceData);
                if (response.ok && response.data?.success) {
                    notify.success('Success', 'Invoice created successfully');
                    onSuccess();
                    handleClose();
                }
                else {
                    notify.error('Error', response.data?.error || 'Failed to create invoice');
                }
            }
        }
        catch (error) {
            notify.error('Error', 'Failed to create invoice');
        }
        finally {
            setLoading(false);
        }
    };
    const handleClose = () => {
        setSelectedContact(null);
        setSelectedMemo(null);
        setContactSearch('');
        setMemoSearch('');
        setLineItems([{ id: '1', description: '', quantity: 1, unitPrice: 0, total: 0 }]);
        setPaymentTerms('Net 30');
        setNotes('');
        onClose();
    };
    if (!isOpen)
        return null;
    return (_jsx(Overlay, { onClick: handleClose, children: _jsxs(Modal, { onClick: e => e.stopPropagation(), children: [_jsxs(Header, { children: [_jsx(Title, { children: "Create New Invoice" }), _jsx(CloseButton, { onClick: handleClose, children: _jsx(FaTimes, {}) })] }), _jsxs(Content, { children: [_jsxs(Section, { children: [_jsxs(SectionTitle, { children: [_jsx(FaUser, {}), " Select Customer"] }), selectedContact ? (_jsxs(SelectedItem, { children: [_jsxs("div", { className: "info", children: [_jsx("div", { className: "name", children: selectedContact.companyName }), _jsxs("div", { className: "detail", children: [selectedContact.contactPerson, " \u2022 ", selectedContact.email] })] }), _jsx("button", { onClick: () => setSelectedContact(null), children: _jsx(FaTimes, {}) })] })) : (_jsxs(SearchContainer, { children: [_jsx(SearchIcon, { children: _jsx(FaSearch, {}) }), _jsx(SearchInput, { placeholder: "Search contacts by name or email...", value: contactSearch, onChange: e => {
                                                setContactSearch(e.target.value);
                                                setShowContactDropdown(true);
                                            }, onFocus: () => setShowContactDropdown(true) }), showContactDropdown && filteredContacts.length > 0 && (_jsx(Dropdown, { children: filteredContacts.slice(0, 5).map(contact => (_jsxs(DropdownItem, { onClick: () => handleSelectContact(contact), children: [_jsx("div", { className: "name", children: contact.companyName }), _jsxs("div", { className: "detail", children: [contact.contactPerson, " \u2022 ", contact.email] })] }, contact.id))) }))] }))] }), _jsxs(Section, { children: [_jsxs(SectionTitle, { children: [_jsx(FaLink, {}), " Link to Memo (Optional)"] }), selectedMemo ? (_jsxs(SelectedItem, { children: [_jsxs("div", { className: "info", children: [_jsxs("div", { className: "name", children: [selectedMemo.diamondDetails.caratWeight, "ct ", selectedMemo.diamondDetails.shape, " ", selectedMemo.diamondDetails.color, " ", selectedMemo.diamondDetails.clarity] }), _jsxs("div", { className: "detail", children: [selectedMemo.dealerName, " \u2022 $", selectedMemo.diamondDetails.priceUSD.toLocaleString()] })] }), _jsx("button", { onClick: handleClearMemo, children: _jsx(FaTimes, {}) })] })) : (_jsxs(SearchContainer, { children: [_jsx(SearchIcon, { children: _jsx(FaSearch, {}) }), _jsx(SearchInput, { placeholder: "Search memos by dealer, diamond shape, carat...", value: memoSearch, onChange: e => {
                                                setMemoSearch(e.target.value);
                                                setShowMemoDropdown(true);
                                            }, onFocus: () => setShowMemoDropdown(true) }), showMemoDropdown && memoSearch && (_jsx(Dropdown, { children: filteredMemos.length > 0 ? (filteredMemos.slice(0, 5).map(memo => (_jsxs(DropdownItem, { onClick: () => handleSelectMemo(memo), children: [_jsxs("div", { className: "name", children: [memo.diamondDetails.caratWeight, "ct ", memo.diamondDetails.shape, " ", memo.diamondDetails.color, " ", memo.diamondDetails.clarity] }), _jsxs("div", { className: "detail", children: [memo.dealerName, " \u2022 $", memo.diamondDetails.priceUSD.toLocaleString()] })] }, memo.id)))) : (_jsx(DropdownItem, { style: { color: 'var(--text-tertiary)', cursor: 'default' }, children: "No matching memos found" })) }))] })), _jsx("div", { style: { fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.5rem' }, children: "Leave empty to create a standalone invoice without linking to a memo" })] }), _jsxs(Section, { children: [_jsxs(SectionTitle, { children: [_jsx(FaFileInvoice, {}), " Line Items"] }), _jsxs(LineItemsTable, { children: [_jsxs(LineItemHeader, { children: [_jsx("div", { children: "Description" }), _jsx("div", { children: "Qty" }), _jsx("div", { children: "Unit Price" }), _jsx("div", { children: "Total" }), _jsx("div", {})] }), lineItems.map(item => (_jsxs(LineItemRow, { children: [_jsx("input", { placeholder: "Item description", value: item.description, onChange: e => handleLineItemChange(item.id, 'description', e.target.value) }), _jsx("input", { type: "number", min: "1", value: item.quantity, onChange: e => handleLineItemChange(item.id, 'quantity', parseInt(e.target.value) || 1) }), _jsx("input", { type: "number", min: "0", step: "0.01", placeholder: "0.00", value: item.unitPrice || '', onChange: e => handleLineItemChange(item.id, 'unitPrice', parseFloat(e.target.value) || 0) }), _jsxs("div", { style: { fontWeight: 500, color: 'var(--text-primary)' }, children: ["$", item.total.toLocaleString()] }), _jsx(DeleteButton, { onClick: () => removeLineItem(item.id), children: _jsx(FaTrash, { size: 12 }) })] }, item.id)))] }), _jsxs(AddButton, { onClick: addLineItem, children: [_jsx(FaPlus, {}), " Add Line Item"] }), _jsxs(TotalsSection, { children: [_jsxs("div", { className: "row", children: [_jsx("span", { className: "label", children: "Subtotal:" }), _jsxs("span", { className: "value", children: ["$", calculateSubtotal().toLocaleString()] })] }), _jsxs("div", { className: "row total", children: [_jsx("span", { className: "label", children: "Total:" }), _jsxs("span", { className: "value", children: ["$", calculateTotal().toLocaleString()] })] })] })] }), _jsxs(Section, { children: [_jsx(FormGrid, { children: _jsxs(FormGroup, { children: [_jsx(Label, { children: "Payment Terms" }), _jsxs(Select, { value: paymentTerms, onChange: e => setPaymentTerms(e.target.value), children: [_jsx("option", { value: "Due on Receipt", children: "Due on Receipt" }), _jsx("option", { value: "Net 15", children: "Net 15" }), _jsx("option", { value: "Net 30", children: "Net 30" }), _jsx("option", { value: "Net 45", children: "Net 45" }), _jsx("option", { value: "Net 60", children: "Net 60" })] })] }) }), _jsxs(FormGroup, { style: { marginTop: '1rem' }, children: [_jsx(Label, { children: "Notes" }), _jsx(Textarea, { placeholder: "Additional notes for the invoice...", value: notes, onChange: e => setNotes(e.target.value) })] })] })] }), _jsxs(Footer, { children: [_jsx(Button, { onClick: handleClose, children: "Cancel" }), _jsx(Button, { primary: true, onClick: handleSubmit, disabled: loading || !selectedContact || lineItems.every(i => !i.description || i.total === 0), children: loading ? 'Creating...' : 'Create Invoice' })] })] }) }));
};
export default CreateInvoiceModal;
